/**
 * MarketTab — Main Marketplace Container
 * 
 * DECKSTR.GG Marketplace Hub with four sub-views:
 * 
 *   PRICING   — Deck cost analysis, per-card pricing, category breakdown
 *   COLLECTION — Personal card binder, trade/sale management, scanning
 *   CART      — Smart multi-vendor shopping cart with price comparison
 *   STORES   — Local game store finder with geolocation
 * 
 * Auto-fetches prices from Scryfall when existing deck data lacks pricing.
 * Stores prices in component state so existing decks work immediately.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Card } from '../App';
import {
    DollarSign, TrendingUp, TrendingDown, ShoppingCart,
    ExternalLink, RefreshCw, ChevronDown, ChevronUp,
    AlertTriangle, Search, Loader2,
    Info, BarChart3, CreditCard, Package, MapPin, Sparkles
} from 'lucide-react';

// Sub-views
import { CollectionView } from './market/CollectionView';
import { CartView } from './market/CartView';
import { ISOListView } from './market/ISOListView';
import { StoreLocator } from './market/StoreLocator';
import {
    MarketView, RARITY_COLORS, CONDITION_MULTIPLIERS,
    formatPrice, buildTCGPlayerCartUrl, buildCardKingdomUrl,
    rateLimitedFetch, CardCondition
} from './market/types';

interface MarketTabProps {
    deck: Card[];
    sideboard?: Card[];
    deckName?: string;
}

// Locally cached price data (for cards missing prices from import)
interface PriceData {
    priceUsd: number | null;
    priceUsdFoil: number | null;
    priceEur: number | null;
    setName?: string;
    setCode?: string;
    purchaseUrlTcg?: string;
    purchaseUrlCardmarket?: string;
    scryfallUrl?: string;
}

type PriceSource = 'usd' | 'eur';
type SortField = 'name' | 'price' | 'cmc' | 'quantity' | 'total';
type SortDir = 'asc' | 'desc';

export function MarketTab({ deck, sideboard = [], deckName = 'Untitled Deck' }: MarketTabProps) {
    // ============== SUB-VIEW NAVIGATION ==============
    const [activeView, setActiveView] = useState<MarketView>('pricing');
    const [currency, setCurrency] = useState<PriceSource>('usd');

    // ============== PRICING STATE ==============
    const [condition, setCondition] = useState<string>('NM');
    const [sortField, setSortField] = useState<SortField>('total');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [searchFilter, setSearchFilter] = useState('');
    const [showSideboard, setShowSideboard] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

    // Local price cache
    const [priceCache, setPriceCache] = useState<Record<string, PriceData>>({});
    const [fetchProgress, setFetchProgress] = useState<{ done: number; total: number } | null>(null);
    const fetchedRef = useRef<Set<string>>(new Set());

    // All cards including sideboard
    const allCards = useMemo(() => {
        const main = deck.filter(c => !c.isExtraCard);
        const side = showSideboard ? sideboard.filter(c => !c.isExtraCard) : [];
        return [...main, ...side];
    }, [deck, sideboard, showSideboard]);

    // Merge card data with local price cache
    const enrichedCards = useMemo(() => {
        return allCards.map(card => {
            const cached = priceCache[card.name];
            if (cached && card.priceUsd == null && card.priceEur == null) {
                return {
                    ...card,
                    priceUsd: cached.priceUsd,
                    priceUsdFoil: cached.priceUsdFoil,
                    priceEur: cached.priceEur,
                    setName: card.setName || cached.setName,
                    setCode: card.setCode || cached.setCode,
                    purchaseUrlTcg: card.purchaseUrlTcg || cached.purchaseUrlTcg,
                    purchaseUrlCardmarket: card.purchaseUrlCardmarket || cached.purchaseUrlCardmarket,
                    scryfallUrl: card.scryfallUrl || cached.scryfallUrl,
                };
            }
            return card;
        });
    }, [allCards, priceCache]);

    // Auto-fetch prices for cards that don't have them
    const fetchMissingPrices = useCallback(async (forceRefresh = false) => {
        const uniqueCards = new Map<string, Card>();
        allCards.forEach(card => {
            if (!uniqueCards.has(card.name)) uniqueCards.set(card.name, card);
        });

        const needsPricing = Array.from(uniqueCards.values()).filter(card => {
            if (forceRefresh) return true;
            if (card.priceUsd != null || card.priceEur != null) return false;
            if (fetchedRef.current.has(card.name)) return false;
            if (priceCache[card.name]) return false;
            return true;
        });

        if (needsPricing.length === 0) return;

        setIsRefreshing(true);
        setFetchProgress({ done: 0, total: needsPricing.length });
        const newCache: Record<string, PriceData> = {};

        for (let i = 0; i < needsPricing.length; i++) {
            const card = needsPricing[i];
            try {
                const name = card.name.includes(' // ') ? card.name.split(' // ')[0] : card.name;
                const response = await rateLimitedFetch(
                    `https://api.scryfall.com/cards/named?exact=${encodeURIComponent(name)}`
                );
                if (response.ok) {
                    const data = await response.json();
                    newCache[card.name] = {
                        priceUsd: data.prices?.usd ? parseFloat(data.prices.usd) : null,
                        priceUsdFoil: data.prices?.usd_foil ? parseFloat(data.prices.usd_foil) : null,
                        priceEur: data.prices?.eur ? parseFloat(data.prices.eur) : null,
                        setName: data.set_name,
                        setCode: data.set,
                        purchaseUrlTcg: data.purchase_uris?.tcgplayer,
                        purchaseUrlCardmarket: data.purchase_uris?.cardmarket,
                        scryfallUrl: data.scryfall_uri,
                    };
                }
                fetchedRef.current.add(card.name);
            } catch (e) {
                console.warn(`Failed to fetch price for ${card.name}`, e);
                fetchedRef.current.add(card.name);
            }
            setFetchProgress({ done: i + 1, total: needsPricing.length });
        }

        setPriceCache(prev => ({ ...prev, ...newCache }));
        setIsRefreshing(false);
        setFetchProgress(null);
    }, [allCards, priceCache]);

    // Auto-fetch on mount
    useEffect(() => {
        if (allCards.length > 0) {
            const hasMissingPrices = allCards.some(card =>
                card.priceUsd == null && card.priceEur == null &&
                !fetchedRef.current.has(card.name) &&
                !priceCache[card.name]
            );
            if (hasMissingPrices) {
                fetchMissingPrices();
            }
        }
    }, [allCards.length]);

    // Get price for a card based on source
    const getCardPrice = (card: Card): number | null => {
        if (currency === 'eur') return card.priceEur ?? null;
        return card.priceUsd ?? null;
    };

    // Apply condition multiplier
    const conditionMultiplier = CONDITION_MULTIPLIERS[condition as CardCondition]?.multiplier ?? 1;
    const adjustedPrice = (price: number | null): number | null => {
        if (price == null) return null;
        return price * conditionMultiplier;
    };

    // Price statistics
    const priceStats = useMemo(() => {
        let totalPrice = 0;
        let pricedCards = 0;
        let unpricedCards = 0;
        let mostExpensive: Card | null = null;
        let mostExpensivePrice = 0;
        let cheapest: Card | null = null;
        let cheapestPrice = Infinity;

        const categoryTotals: Record<string, { count: number; total: number; cards: Card[] }> = {
            Creatures: { count: 0, total: 0, cards: [] },
            Instants: { count: 0, total: 0, cards: [] },
            Sorceries: { count: 0, total: 0, cards: [] },
            Enchantments: { count: 0, total: 0, cards: [] },
            Artifacts: { count: 0, total: 0, cards: [] },
            Planeswalkers: { count: 0, total: 0, cards: [] },
            Lands: { count: 0, total: 0, cards: [] },
            Other: { count: 0, total: 0, cards: [] },
        };

        const rarityTotals: Record<string, { count: number; total: number }> = {
            M: { count: 0, total: 0 }, R: { count: 0, total: 0 },
            U: { count: 0, total: 0 }, C: { count: 0, total: 0 },
        };

        enrichedCards.forEach(card => {
            const basePrice = getCardPrice(card);
            const price = adjustedPrice(basePrice);

            if (price != null) {
                const cardTotal = price * card.quantity;
                totalPrice += cardTotal;
                pricedCards += card.quantity;
                if (price > mostExpensivePrice) { mostExpensivePrice = price; mostExpensive = card; }
                if (price < cheapestPrice) { cheapestPrice = price; cheapest = card; }
            } else {
                unpricedCards += card.quantity;
            }

            let category = 'Other';
            if (card.isLand) category = 'Lands';
            else if (card.type?.includes('Creature')) category = 'Creatures';
            else if (card.type?.includes('Instant')) category = 'Instants';
            else if (card.type?.includes('Sorcery')) category = 'Sorceries';
            else if (card.type?.includes('Enchantment')) category = 'Enchantments';
            else if (card.type?.includes('Artifact')) category = 'Artifacts';
            else if (card.type?.includes('Planeswalker')) category = 'Planeswalkers';

            if (categoryTotals[category]) {
                categoryTotals[category].count += card.quantity;
                categoryTotals[category].total += (adjustedPrice(getCardPrice(card)) ?? 0) * card.quantity;
                categoryTotals[category].cards.push(card);
            }

            const rarity = card.rarity || 'C';
            if (rarityTotals[rarity]) {
                rarityTotals[rarity].count += card.quantity;
                rarityTotals[rarity].total += (adjustedPrice(getCardPrice(card)) ?? 0) * card.quantity;
            }
        });

        return {
            totalPrice, pricedCards, unpricedCards,
            mostExpensive: mostExpensive as Card | null,
            mostExpensivePrice: mostExpensive ? (adjustedPrice(getCardPrice(mostExpensive)) ?? 0) : 0,
            cheapest: cheapest as Card | null,
            cheapestPrice: cheapest ? (cheapestPrice === Infinity ? 0 : (adjustedPrice(getCardPrice(cheapest)) ?? 0)) : 0,
            categoryTotals, rarityTotals,
            avgPerCard: pricedCards > 0 ? totalPrice / pricedCards : 0,
        };
    }, [enrichedCards, condition, currency]);

    // Sorted & filtered card list
    const sortedCards = useMemo(() => {
        let filtered = enrichedCards.filter(c => {
            if (!searchFilter) return true;
            return c.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
                c.type?.toLowerCase().includes(searchFilter.toLowerCase());
        });

        filtered.sort((a, b) => {
            let aVal: number | string = 0;
            let bVal: number | string = 0;
            switch (sortField) {
                case 'name':
                    return sortDir === 'asc'
                        ? a.name.localeCompare(b.name)
                        : b.name.localeCompare(a.name);
                case 'price':
                    aVal = adjustedPrice(getCardPrice(a)) ?? -1;
                    bVal = adjustedPrice(getCardPrice(b)) ?? -1;
                    break;
                case 'cmc': aVal = a.cmc; bVal = b.cmc; break;
                case 'quantity': aVal = a.quantity; bVal = b.quantity; break;
                case 'total':
                    aVal = (adjustedPrice(getCardPrice(a)) ?? 0) * a.quantity;
                    bVal = (adjustedPrice(getCardPrice(b)) ?? 0) * b.quantity;
                    break;
            }
            return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
        });

        return filtered;
    }, [enrichedCards, searchFilter, sortField, sortDir, condition, currency]);

    // Toggle sort
    const toggleSort = (field: SortField) => {
        if (sortField === field) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir(field === 'name' ? 'asc' : 'desc'); }
    };

    const hasPriceData = enrichedCards.some(c => c.priceUsd != null || c.priceEur != null);

    // Sub-view definitions
    const subViews: { id: MarketView; label: string; icon: any }[] = [
        { id: 'pricing', label: 'PRICING', icon: DollarSign },
        { id: 'collection', label: 'COLLECTION', icon: Package },
        { id: 'cart', label: 'CART', icon: ShoppingCart },
        { id: 'iso', label: 'ISO LIST', icon: Sparkles },
        { id: 'stores', label: 'STORES', icon: MapPin },
    ];

    return (
        <div className="space-y-3">
            {/* ============================================ */}
            {/* SUB-NAVIGATION                               */}
            {/* ============================================ */}
            <div className="flex items-center gap-1 bg-white/[0.03] border border-white/10 rounded-xl p-1">
                {subViews.map(view => {
                    const Icon = view.icon;
                    const isActive = activeView === view.id;
                    return (
                        <button
                            key={view.id}
                            onClick={() => setActiveView(view.id)}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${isActive
                                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 shadow-sm shadow-amber-500/10'
                                : 'text-white/40 hover:text-white/60 hover:bg-white/5 border border-transparent'
                                }`}
                        >
                            <Icon className="size-3.5" />
                            {view.label}
                        </button>
                    );
                })}
            </div>

            {/* ============================================ */}
            {/* PRICING VIEW (default)                       */}
            {/* ============================================ */}
            {activeView === 'pricing' && (
                <div className="space-y-4">
                    {/* Loading state */}
                    {fetchProgress && (
                        <div className="bg-amber-900/20 border border-amber-500/20 rounded-xl p-4">
                            <div className="flex items-center gap-3 mb-2">
                                <Loader2 className="size-4 text-amber-400 animate-spin" />
                                <span className="text-sm font-medium text-amber-400">Fetching prices from Scryfall...</span>
                                <span className="text-xs text-white/40 ml-auto tabular-nums">{fetchProgress.done} / {fetchProgress.total}</span>
                            </div>
                            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-300 bg-gradient-to-r from-amber-500 to-amber-400"
                                    style={{ width: `${(fetchProgress.done / fetchProgress.total) * 100}%` }} />
                            </div>
                        </div>
                    )}

                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-900/30 via-indigo-900/20 to-purple-900/30 border border-purple-500/20 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                                    <DollarSign className="size-5 text-amber-400" />
                                </div>
                                <div>
                                    <div className="text-xs text-white/40 uppercase tracking-wider">Estimated Deck Cost</div>
                                    <div className="text-2xl font-bold text-amber-400 tabular-nums">
                                        {hasPriceData ? formatPrice(priceStats.totalPrice, currency) : (isRefreshing ? '...' : '—')}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <select value={condition} onChange={e => setCondition(e.target.value)}
                                    className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-purple-500/50">
                                    {Object.entries(CONDITION_MULTIPLIERS).map(([key, val]) => (
                                        <option key={key} value={key}>{key} ({Math.round(val.multiplier * 100)}%)</option>
                                    ))}
                                </select>
                                <button onClick={() => setCurrency(prev => prev === 'usd' ? 'eur' : 'usd')}
                                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border ${currency === 'usd' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'}`}>
                                    {currency === 'usd' ? '$ USD' : '€ EUR'}
                                </button>
                            </div>
                        </div>

                        {/* Stats row */}
                        <div className="grid grid-cols-4 gap-3">
                            <div className="bg-black/20 rounded-lg px-3 py-2 border border-white/5">
                                <div className="text-[10px] text-white/40 uppercase">Cards Priced</div>
                                <div className="text-sm font-medium text-white">
                                    {priceStats.pricedCards}
                                    {priceStats.unpricedCards > 0 && <span className="text-amber-400 text-[10px] ml-1">+{priceStats.unpricedCards} unpriced</span>}
                                </div>
                            </div>
                            <div className="bg-black/20 rounded-lg px-3 py-2 border border-white/5">
                                <div className="text-[10px] text-white/40 uppercase">Avg / Card</div>
                                <div className="text-sm font-medium text-white tabular-nums">{formatPrice(priceStats.avgPerCard, currency)}</div>
                            </div>
                            <div className="bg-black/20 rounded-lg px-3 py-2 border border-white/5">
                                <div className="text-[10px] text-white/40 uppercase flex items-center gap-1"><TrendingUp className="size-3 text-red-400" /> Most Expensive</div>
                                <div className="text-sm font-medium text-white truncate" title={priceStats.mostExpensive?.name || ''}>
                                    {priceStats.mostExpensive ? `${priceStats.mostExpensive.name.split(' // ')[0]} · ${formatPrice(priceStats.mostExpensivePrice, currency)}` : '—'}
                                </div>
                            </div>
                            <div className="bg-black/20 rounded-lg px-3 py-2 border border-white/5">
                                <div className="text-[10px] text-white/40 uppercase flex items-center gap-1"><TrendingDown className="size-3 text-green-400" /> Cheapest</div>
                                <div className="text-sm font-medium text-white truncate" title={priceStats.cheapest?.name || ''}>
                                    {priceStats.cheapest ? `${priceStats.cheapest.name.split(' // ')[0]} · ${formatPrice(priceStats.cheapestPrice, currency)}` : '—'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Buy buttons */}
                    <div className="grid grid-cols-2 gap-3">
                        <a href={buildTCGPlayerCartUrl(enrichedCards)} target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-600/80 to-orange-500/80 hover:from-orange-500 hover:to-orange-400 text-white rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] border border-orange-400/30 shadow-lg shadow-orange-500/10">
                            <ShoppingCart className="size-4" /> Buy on TCGPlayer <ExternalLink className="size-3 opacity-60" />
                        </a>
                        <a href={buildCardKingdomUrl(deckName)} target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600/80 to-blue-500/80 hover:from-blue-500 hover:to-blue-400 text-white rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] border border-blue-400/30 shadow-lg shadow-blue-500/10">
                            <CreditCard className="size-4" /> Buy on Card Kingdom <ExternalLink className="size-3 opacity-60" />
                        </a>
                    </div>

                    {/* Category breakdown */}
                    <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
                        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <BarChart3 className="size-4 text-white/40" />
                                <span className="text-sm font-medium text-white/80">Cost by Category</span>
                            </div>
                            <div className="flex items-center gap-4">
                                {Object.entries(priceStats.rarityTotals).filter(([_, v]) => v.count > 0).map(([rarity, data]) => (
                                    <div key={rarity} className="flex items-center gap-1.5 text-[10px]"
                                        title={`${rarity === 'M' ? 'Mythic' : rarity === 'R' ? 'Rare' : rarity === 'U' ? 'Uncommon' : 'Common'}: ${data.count} cards, ${formatPrice(data.total, currency)}`}>
                                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: RARITY_COLORS[rarity]?.text }} />
                                        <span className="text-white/40">{data.count}× {formatPrice(data.total, currency)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="divide-y divide-white/5">
                            {Object.entries(priceStats.categoryTotals).filter(([_, d]) => d.count > 0).sort((a, b) => b[1].total - a[1].total)
                                .map(([category, data]) => {
                                    const pct = priceStats.totalPrice > 0 ? (data.total / priceStats.totalPrice) * 100 : 0;
                                    const isExpanded = expandedCategory === category;
                                    return (
                                        <div key={category}>
                                            <button onClick={() => setExpandedCategory(isExpanded ? null : category)}
                                                className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-white/[0.02] transition-colors">
                                                <span className="text-xs text-white/60 w-24 text-left">{category}</span>
                                                <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                                                    <div className="h-full rounded-full transition-all duration-500"
                                                        style={{ width: `${Math.max(pct, 1)}%`, background: `linear-gradient(90deg, rgba(168,85,247,0.6), rgba(139,92,246,0.4))` }} />
                                                </div>
                                                <span className="text-xs text-white/40 w-10 text-right tabular-nums">{data.count}</span>
                                                <span className="text-xs text-amber-400 font-medium w-16 text-right tabular-nums">{formatPrice(data.total, currency)}</span>
                                                <span className="text-[10px] text-white/30 w-10 text-right tabular-nums">{pct.toFixed(0)}%</span>
                                                {isExpanded ? <ChevronUp className="size-3 text-white/30" /> : <ChevronDown className="size-3 text-white/30" />}
                                            </button>
                                            {isExpanded && (
                                                <div className="bg-black/20 px-4 py-2 space-y-1">
                                                    {data.cards.sort((a, b) => {
                                                        const aP = (adjustedPrice(getCardPrice(a)) ?? 0) * a.quantity;
                                                        const bP = (adjustedPrice(getCardPrice(b)) ?? 0) * b.quantity;
                                                        return bP - aP;
                                                    }).map(card => {
                                                        const price = adjustedPrice(getCardPrice(card));
                                                        return (
                                                            <div key={card.id} className="flex items-center gap-2 py-0.5">
                                                                <span className="text-[10px] text-white/30 w-5 text-right">{card.quantity}×</span>
                                                                <span className="text-xs text-white/70 flex-1 truncate">{card.name.split(' // ')[0]}</span>
                                                                <span className="text-[10px] px-1.5 py-0.5 rounded"
                                                                    style={{ backgroundColor: RARITY_COLORS[card.rarity || 'C']?.bg, color: RARITY_COLORS[card.rarity || 'C']?.text }}>
                                                                    {card.rarity || 'C'}
                                                                </span>
                                                                <span className="text-xs text-white/40 tabular-nums w-14 text-right">{formatPrice(price, currency)}</span>
                                                                <span className="text-xs text-amber-400/80 font-medium tabular-nums w-16 text-right">
                                                                    {formatPrice(price != null ? price * card.quantity : null, currency)}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    </div>

                    {/* Card list table */}
                    <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden">
                        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-white/30" />
                                <input type="text" value={searchFilter} onChange={e => setSearchFilter(e.target.value)}
                                    placeholder="Filter cards..." className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50" />
                            </div>
                            <label className="flex items-center gap-1.5 text-[10px] text-white/40 cursor-pointer">
                                <input type="checkbox" checked={showSideboard} onChange={e => setShowSideboard(e.target.checked)}
                                    className="rounded bg-white/10 border-white/20 text-amber-500 focus:ring-0 w-3 h-3" />
                                Sideboard
                            </label>
                            <button onClick={() => fetchMissingPrices(true)} disabled={isRefreshing}
                                className="flex items-center gap-1 px-2 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-[10px] text-white/40 hover:text-white/70 transition-colors disabled:opacity-50">
                                <RefreshCw className={`size-3 ${isRefreshing ? 'animate-spin' : ''}`} /> Refresh
                            </button>
                        </div>

                        {/* Table header */}
                        <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2 text-[10px] text-white/30 uppercase tracking-wider">
                            <button onClick={() => toggleSort('name')} className="flex items-center gap-0.5 flex-1 hover:text-white/60">
                                Card {sortField === 'name' && (sortDir === 'asc' ? <ChevronUp className="size-2.5" /> : <ChevronDown className="size-2.5" />)}
                            </button>
                            <button onClick={() => toggleSort('cmc')} className="flex items-center gap-0.5 w-8 text-center hover:text-white/60">
                                MV {sortField === 'cmc' && (sortDir === 'asc' ? <ChevronUp className="size-2.5" /> : <ChevronDown className="size-2.5" />)}
                            </button>
                            <button onClick={() => toggleSort('quantity')} className="flex items-center gap-0.5 w-8 text-center hover:text-white/60">
                                Qty {sortField === 'quantity' && (sortDir === 'asc' ? <ChevronUp className="size-2.5" /> : <ChevronDown className="size-2.5" />)}
                            </button>
                            <span className="w-14 text-right">Set</span>
                            <button onClick={() => toggleSort('price')} className="flex items-center gap-0.5 w-16 justify-end hover:text-white/60">
                                Price {sortField === 'price' && (sortDir === 'asc' ? <ChevronUp className="size-2.5" /> : <ChevronDown className="size-2.5" />)}
                            </button>
                            <button onClick={() => toggleSort('total')} className="flex items-center gap-0.5 w-16 justify-end hover:text-white/60">
                                Total {sortField === 'total' && (sortDir === 'asc' ? <ChevronUp className="size-2.5" /> : <ChevronDown className="size-2.5" />)}
                            </button>
                            <span className="w-8" />
                        </div>

                        {/* Card rows */}
                        <div className="max-h-[400px] overflow-y-auto divide-y divide-white/[0.03]">
                            {sortedCards.length === 0 ? (
                                <div className="px-4 py-8 text-center text-sm text-white/30">
                                    {isRefreshing ? 'Loading prices...' : searchFilter ? 'No cards match your filter' : 'No cards found'}
                                </div>
                            ) : sortedCards.map(card => {
                                const price = adjustedPrice(getCardPrice(card));
                                const total = price != null ? price * card.quantity : null;
                                const rarityColor = RARITY_COLORS[card.rarity || 'C'];
                                const displayName = card.name.includes(' // ') ? card.name.split(' // ')[0] : card.name;

                                return (
                                    <div key={card.id} className="px-4 py-1.5 flex items-center gap-2 hover:bg-white/[0.02] transition-colors group">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: rarityColor.text }} />
                                            <span className="text-xs text-white/80 truncate" title={card.name}>{displayName}</span>
                                            {card.isSideboard && <span className="text-[8px] px-1 py-0 bg-amber-500/20 text-amber-400 rounded border border-amber-500/30">SB</span>}
                                            {card.isCommander && <span className="text-[8px] px-1 py-0 bg-purple-500/20 text-purple-400 rounded border border-purple-500/30">CMD</span>}
                                        </div>
                                        <span className="text-[10px] text-white/30 w-8 text-center tabular-nums">{card.isLand ? '—' : card.cmc}</span>
                                        <span className="text-[10px] text-white/40 w-8 text-center tabular-nums">×{card.quantity}</span>
                                        <span className="text-[10px] text-white/25 w-14 text-right uppercase truncate" title={card.setName || ''}>{card.setCode || '—'}</span>
                                        <span className={`text-xs w-16 text-right tabular-nums ${price != null ? 'text-white/60' : 'text-white/20'}`}>{formatPrice(price, currency)}</span>
                                        <span className={`text-xs font-medium w-16 text-right tabular-nums ${total != null && total >= 20 ? 'text-red-400/80' : total != null && total >= 5 ? 'text-amber-400/80' : total != null ? 'text-emerald-400/80' : 'text-white/20'
                                            }`}>{formatPrice(total, currency)}</span>
                                        <a href={card.purchaseUrlTcg || buildCardKingdomUrl(card.name)} target="_blank" rel="noopener noreferrer"
                                            className="w-8 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Buy this card">
                                            <ExternalLink className="size-3 text-amber-400/60 hover:text-amber-400" />
                                        </a>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between bg-purple-900/10">
                            <span className="text-xs text-white/40">
                                {sortedCards.length} card{sortedCards.length !== 1 ? 's' : ''}
                                {searchFilter && ` matching "${searchFilter}"`}
                            </span>
                            <div className="flex items-center gap-3">
                                {condition !== 'NM' && (
                                    <span className="text-xs text-amber-400/60">
                                        {condition} ({Math.round(conditionMultiplier * 100)}%)
                                    </span>
                                )}
                                <span className="text-sm font-bold text-amber-400 tabular-nums">{formatPrice(priceStats.totalPrice, currency)}</span>
                            </div>
                        </div>
                    </div>

                    {/* No price data warning */}
                    {!hasPriceData && !isRefreshing && (
                        <div className="bg-amber-900/20 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
                            <AlertTriangle className="size-5 text-amber-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="text-sm font-medium text-amber-400 mb-1">No Price Data Available</div>
                                <p className="text-xs text-white/50">Price data could not be fetched. Check your internet connection and click "Refresh" to try again.</p>
                            </div>
                        </div>
                    )}

                    {/* Info footer */}
                    <div className="flex items-start gap-2 px-1 text-[10px] text-white/25">
                        <Info className="size-3 flex-shrink-0 mt-0.5" />
                        <p>Prices sourced from Scryfall (TCGPlayer market data). Actual prices may vary. Affiliate links support DECKSTR.GG at no extra cost.</p>
                    </div>
                </div>
            )}

            {/* ============================================ */}
            {/* COLLECTION VIEW                              */}
            {/* ============================================ */}
            {activeView === 'collection' && (
                <CollectionView currency={currency} />
            )}

            {/* ============================================ */}
            {/* CART VIEW                                    */}
            {/* ============================================ */}
            {activeView === 'cart' && (
                <CartView
                    deck={enrichedCards}
                    sideboard={sideboard}
                    deckName={deckName}
                    currency={currency}
                />
            )}

            {/* ============================================ */}
            {/* ISO LIST VIEW                                */}
            {/* ============================================ */}
            {activeView === 'iso' && (
                <ISOListView currency={currency} />
            )}

            {/* ============================================ */}
            {/* STORE LOCATOR VIEW                           */}
            {/* ============================================ */}
            {activeView === 'stores' && (
                <StoreLocator currency={currency} />
            )}
        </div>
    );
}
